/**
 * useLocalTtsInstallStatus Hook
 *
 * Answers one question, once: for each TTS provider that HAS an installable
 * local component, is that component actually installed on this machine right
 * now?
 *
 * Every screen that prices TTS must agree on this, because they render next to
 * each other and describe the same run: the TTS picker card
 * (TTSSourceSelector), the video-pipeline estimate (VideoConfigForm ->
 * PipelineCostEstimator) and the standalone Audio tab estimate
 * (AudioConfigForm -> AudioEstimator, which takes `installed` as an explicit
 * request parameter). They used to each derive it themselves and drifted:
 * Kokoro was wired into all three, F5-TTS into none, so installing F5-TTS
 * locally still showed and charged the cloud price (RULE #-2: one
 * implementation, not three aligned ones).
 *
 * Returns a record keyed by PROVIDER id. Callers that need the COMPONENT id
 * (the pipeline availability map is keyed that way, because the estimator's
 * _resolve_component_id rewrites kokoro -> kokoro_tts) must read it from
 * TTS_SOURCE_COMPONENT_IDS rather than assuming the two ids are equal.
 *
 * "Installed" here is `is_installed`, not `is_running`: the backend auto-loads
 * a TTS model on demand at synthesis time, exactly like the local image and
 * video components. `useComponentStatus` reports null in pure web/cloud mode
 * and while the first poll is in flight, which collapses to false — the only
 * honest default, since a provider whose local state is unknown must be
 * quoted at the cloud rate it will actually be billed at (RULE #5).
 */

import { useMemo } from 'react';
import { useComponentStatus } from './useComponentStatus';
import { TTS_SOURCE_COMPONENT_IDS, type LocalTtsProviderId } from '../constants/ttsSources';

export type LocalTtsInstallStatus = Readonly<Record<LocalTtsProviderId, boolean>>;

export function useLocalTtsInstallStatus(): LocalTtsInstallStatus {
  // One useComponentStatus call per component, spelled out because the Rules of
  // Hooks forbid iterating TTS_SOURCE_COMPONENT_IDS to make them. The return
  // type is keyed by `keyof typeof TTS_SOURCE_COMPONENT_IDS`, so adding a
  // provider to that constant breaks THIS file at compile time until its poll
  // is added here — which is the whole point: the list cannot silently grow
  // past the code that reads it.
  const kokoroInstalled =
    useComponentStatus(TTS_SOURCE_COMPONENT_IDS.kokoro)?.is_installed === true;
  const f5TtsInstalled = useComponentStatus(TTS_SOURCE_COMPONENT_IDS.f5_tts)?.is_installed === true;

  return useMemo(
    () => ({
      kokoro: kokoroInstalled,
      f5_tts: f5TtsInstalled,
    }),
    [kokoroInstalled, f5TtsInstalled]
  );
}

export default useLocalTtsInstallStatus;
